import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Checkbox } from "../../ui/checkbox";
import { ChevronDown } from "lucide-react";

/**
 * Dropdown multi-select for additional technicians.
 * options: [{ id, name }], selected: [id], onChange(nextIds)
 */
export const MultiTechSelect = ({
  options,
  selected = [],
  onChange,
  placeholder = "None",
  triggerClassName = "",
  testIdPrefix = "additional-tech",
}) => {
  const [open, setOpen] = useState(false);
  const selectedNames = options.filter((o) => selected.includes(o.id)).map((o) => o.name);

  const toggle = (id, checked) => {
    onChange(checked ? [...selected, id] : selected.filter((s) => s !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${triggerClassName}`}
          data-testid={`${testIdPrefix}-select`}
        >
          <span className={`truncate text-left ${selectedNames.length ? "text-foreground" : "text-muted-foreground"}`}>
            {selectedNames.length ? selectedNames.join(", ") : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-1 bg-popover border-border z-[70]"
        style={{ width: "var(--radix-popover-trigger-width)" }}
        align="start"
      >
        {options.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-1.5">No other active technicians</p>
        ) : (
          <div className="max-h-56 overflow-y-auto overscroll-contain touch-pan-y">
            {options.map((o) => {
              const isSelected = selected.includes(o.id);
              return (
                <label
                  key={o.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${isSelected ? "bg-muted/60 text-foreground" : "text-muted-foreground hover:bg-muted/40"}`}
                  data-testid={`${testIdPrefix}-option-${o.id}`}
                >
                  <Checkbox checked={isSelected} onCheckedChange={(v) => toggle(o.id, !!v)} />
                  <span className="truncate">{o.name}</span>
                </label>
              );
            })}
          </div>
        )}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full mt-1 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 rounded-md text-left border-t border-border"
            data-testid={`${testIdPrefix}-clear`}
          >
            Clear selection
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MultiTechSelect;
